import {
  CheckboxTypes,
  Icon,
  SliderModal,
  SliderModalTypes,
  Text,
  UI,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, Button, Pressable, ScrollView } from 'native-base';
import React, { useEffect, useState } from 'react';
import { Dimensions } from 'react-native';
import { RecoilState, useRecoilState } from 'recoil';

import { TableHeadItemOrderBy } from '../TableHead';

import { sortDirectionOptions } from './constants';

export declare namespace OrderByModalTypes {
  export interface OrderByStructure extends CheckboxTypes.Option {
    preventVariationLabel?: boolean;
    /** Custom characters for descending and ascending order, respectively.
     * But only one character can be provided for both directions. */
    customChar?: [string] | [string, string];
    hideDefaultIcon?: boolean;
  }

  export type OrderByType = OrderByStructure[];

  interface OrderByAtom {
    availableOrderBy: OrderByModalTypes.OrderByType;
    selectedOrderBy: TableHeadItemOrderBy | undefined;
    appliedOrderBy: TableHeadItemOrderBy | undefined;
  }

  export interface Props {
    onClose: () => void;
    orderByAtom: RecoilState<OrderByAtom>;
    modalInsideModal?: SliderModalTypes.Props['modalInsideModal'];
    /**
     * Width of the modal
     * @default: 0.3
     */
    sliderModalWidth?: number;
  }

  export interface SelectedOrderByProps {
    fieldName: string;
  }
}

const maxH = Dimensions.get('screen').height * 0.5;

export const OrderByModal = ({
  onClose,
  orderByAtom,
  sliderModalWidth = 0.3,
  modalInsideModal = false,
}: OrderByModalTypes.Props) => {
  const direction = useBreakpointValue({ base: 'bottom', lg: 'right' } as const);
  const [clearingFilters, setClearingFilters] = useState(false);
  const [{ selectedOrderBy: _, availableOrderBy, appliedOrderBy }, setOrdersByState] =
    useRecoilState(orderByAtom);

  const [sortBy, setSortBy] = useState(appliedOrderBy?.value || '');
  const [sortDirection, setSortDirection] = useState(appliedOrderBy?.direction || 'asc');

  const directionIsBottom = direction === 'bottom';

  const handleChangeSortBy = (_value: string) => {
    setSortBy(_value);
  };

  const handleChangeSortDirection = (_value: string) => {
    setSortDirection(_value as 'asc' | 'desc');
  };

  const handleApplyFilters = () => {
    setOrdersByState((prev) => ({
      ...prev,
      appliedOrderBy: { direction: sortDirection, value: sortBy },
    }));
    onClose();
  };

  const handleClearFilters = () => {
    setSortBy('');
    setSortDirection('asc');
    setClearingFilters(true);
    setOrdersByState((prev) => ({
      ...prev,
      appliedOrderBy: undefined,
    }));
    onClose();
  };

  const renderOptions = () => {
    return (
      <VStack space="stack.xxxs">
        <VStack space={Spacing['stack.xxxs']}>
          <Text variant="semibold" color={'neutral.darkest'} fontSize={Typography['fontsize.md']}>
            Ordenar por
          </Text>

          <UI.CheckboxAsPressable
            testID="order-by-checkbox-as-pressable"
            options={availableOrderBy}
            selections={[sortBy]}
            onPress={(value) => handleChangeSortBy(value)}
          />
        </VStack>
        <VStack space={Spacing['stack.xxxs']}>
          <Text variant="semibold" color={'neutral.darkest'} fontSize={Typography['fontsize.md']}>
            Ordem de exibição
          </Text>
          <UI.CheckboxAsPressable
            testID="direction-checkbox-as-pressable"
            options={sortDirectionOptions}
            selections={[sortDirection]}
            onPress={(value) => handleChangeSortDirection(value)}
          />
        </VStack>
      </VStack>
    );
  };

  const hasSelection = !!sortBy && !!sortDirection;

  useEffect(() => {
    if (clearingFilters) {
      setClearingFilters(false);
    }
  }, [clearingFilters]);

  useEffect(() => {
    setOrdersByState((prev) => ({
      ...prev,
      selectedOrderBy: { direction: sortDirection, value: sortBy },
    }));
  }, [setOrdersByState, sortBy, sortDirection]);

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={onClose}
      direction={direction}
      width={sliderModalWidth}
      modalInsideModal={modalInsideModal}>
      <VStack
        w="full"
        py={Spacing['inline.xxxs']}
        borderTopRadius={{ base: Spacing['inline.xxxs'], lg: 0 }}
        backgroundColor={Color['neutral.white']}
        space={Spacing['inline.xxxs']}>
        {directionIsBottom && (
          <Box
            w="55px"
            alignSelf="center"
            background={'neutral.lightest'}
            borderRadius={BoxRadius.sm}
            h={1}
          />
        )}
        <HStack
          borderTopRadius={{ base: BoxRadius.md, lg: 0 }}
          bg="neutral.white"
          px={Spacing['inline.xxs']}
          space={Spacing['stack.quarck']}
          alignItems="center"
          justifyContent={'space-between'}>
          <Text
            variant="bold"
            fontSize={Typography['fontsize.lg']}
            lineHeight="distant"
            color="neutral.black">
            Escolha o tipo de ordenação
          </Text>
          <Pressable testID={'button-close-filter-screen'} onPress={onClose}>
            <Icon
              testID={'icon-close-filter-screen'}
              name="Close"
              size="xs"
              variant="rounded"
              {...(direction !== 'bottom' && { borderWidth: 0.05 })}
              backgroundColor="transparent"
              color={Color['brand.primary.principal']}
              borderColor={Color['brand.primary.principal']}
            />
          </Pressable>
        </HStack>
      </VStack>

      <ScrollView showsVerticalScrollIndicator={false} {...(directionIsBottom && { maxH })}>
        <VStack
          backgroundColor={Color['neutral.white']}
          px={Spacing['inline.xxs']}
          pb={Spacing['inline.xxs']}
          space={Spacing['stack.xxxs']}>
          {renderOptions()}
        </VStack>
      </ScrollView>
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        bg="neutral.white"
        alignItems={'center'}
        justifyContent={'space-between'}
        p={Spacing['inline.xxs']}
        borderTopWidth={1}
        w={'full'}
        borderTopColor={Color['neutral.lightest']}>
        <Button
          onPress={handleClearFilters}
          variant={'textLink'}
          w={{ base: 'full', lg: 'container' }}
          isDisabled={!hasSelection}
          _text={{
            color: hasSelection ? 'feedback.error.default' : 'neutral.light',
            fontFamily: Typography['fontfamily.base.semibold'],
          }}
          rightIcon={
            <Icon
              testID="testID"
              name="Trash"
              color={Color[hasSelection ? 'feedback.error.default' : 'neutral.light']}
              size="xxs"
            />
          }>
          {directionIsBottom ? 'remover ordenação' : 'Limpar ordenação'}
        </Button>
        <Button
          w={{ base: 'full', lg: 'container' }}
          variant="primary"
          onPress={handleApplyFilters}
          isDisabled={!hasSelection}>
          Aplicar ordenação
        </Button>
      </Stack>
    </SliderModal>
  );
};
